/* eslint-disable no-underscore-dangle */
/**
 * `/portal` — the driver's day.
 *
 * A week strip picks the day (today by default, kept in the URL as
 * `?nap=YYYY-MM-DD` so a reload or the back button keeps it). Today opens with
 * one hero for the trip that matters right now, then the rest of the day as a
 * list; once nothing is left today, the next trip on a later day is pointed
 * out under it. Any other day — past or ahead — is just its list, read-only.
 *
 * Everything here is read from the scrubbed `/portal/trips` payload: no
 * prices, no operator margins, nothing commercial ever reaches this screen.
 *
 * The one exception is the driver's own expenses (parking tickets and the
 * like): the amounts in "Rögzített költségek" are what the driver typed in as
 * their claim, not anything the booking costs.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useUrlViewState from '../../Hooks/useUrlViewState';
import {
  fetchPortalExpenses,
  fetchPortalSession,
  fetchPortalTrips,
  getPortalPrincipal,
} from '../../Services/PortalApi';
import { formatDate, formatTime } from '../../Utils/timezone';
import { navigationUrl } from '../../Utils/geoPoint';
import { telUrl } from '../../Utils/contactLinks';
import {
  addDays,
  byTime,
  countdown,
  dayLabel,
  firstName,
  initials,
  isCalendarDay,
  longDate,
  navigationTarget,
  nextTrip as pickNextTrip,
  NOW_LABEL,
  officeMessage,
  passengerChips,
  portalLoadErrorMessage,
  principalName,
  vehicleLabel,
  weekOf,
} from './portalFormat';
import NowLine, { NOW_LINE_PORTAL } from '../../Components/NowLine';
import { nowSplitIndex, nowTimeLabel } from '../../Utils/nowMarker';
import useNow from './useNow';
import signOutOfPortal from './signOutOfPortal';
import RouteRail, { endpointStops } from './Components/RouteRail';
import TripRow from './Components/TripRow';
import RecordExpense from './Components/RecordExpense';
import ExpenseList from './Components/ExpenseList';
import WeekStrip from './Components/WeekStrip';
import DayPickerSheet from './Components/DayPickerSheet';
import { NavigateIcon, PhoneIcon } from './Components/Icons';
import BrandLogo from './Components/BrandLogo';
import OfficeWhatsAppButton from './Components/OfficeWhatsAppButton';
import PushSetting from './Components/PushSetting';
import PushAsk from './Components/PushAsk';

/**
 * The selected day, in the URL — read and written through
 * `Hooks/useUrlViewState`, the same mechanism the dashboard's lists use.
 *
 * It was already `?nap=`, which is why Back has always kept the day; what
 * it was not was careful about its neighbours. Clearing the day wrote
 * `setSearchParams({})`, which does not clear the day — it clears the
 * WHOLE query string, and would take any param added beside it with it.
 * That is the exact trap `useUrlViewState.dropParams` was written for,
 * sitting here unsprung only because nothing else writes to this URL yet.
 */
const DAY_PARAM = 'nap';
const DAY_VIEW = { [DAY_PARAM]: '' };

// A home-screen app stays open for hours: the countdown ("35 perc múlva") and
// "today" are re-read this often.
const CLOCK_MS = 30 * 1000;

// Once today has nothing left to drive, how far ahead to look for the next trip.
const LOOKAHEAD_DAYS = 14;

function Hero({ trip, onOpen, now }) {
  // The car by name when the API resolved it, else the booked category.
  const car = vehicleLabel(trip) || trip.category || '';
  const chips = passengerChips(trip);
  if (trip.flight_number) chips.push(trip.flight_number);

  const when = countdown(trip.date_datetime, now) || dayLabel(trip.date_datetime, now);
  // Where the driver is heading: the pickup, or once the guest is aboard the drop-off.
  const navigation = navigationUrl(navigationTarget(trip, now).point);
  const phone = telUrl(trip.customer_phone);

  return (
    <div className="pp-hero">
      <div className="pp-hero__top">
        <span className="pp-hero__label">Következő út</span>
        {when ? <span className="pp-hero__pill">{when}</span> : null}
      </div>

      {/* One control, not two: the whole block opens the trip. */}
      <button type="button" className="pp-hero__open" onClick={() => onOpen(trip)}>
        <span className="pp-hero__when">
          <span className="pp-hero__time">{formatTime(trip.date_datetime)}</span>
          {car ? <span className="pp-hero__car">{car}</span> : null}
        </span>
        <RouteRail stops={endpointStops(trip)} />
      </button>

      {chips.length ? (
        <div className="pp-chips">
          {chips.map((chip) => <span className="pp-chip" key={chip}>{chip}</span>)}
        </div>
      ) : null}

      <div className="pp-actions">
        {navigation ? (
          <a
            className="pp-btn pp-btn--primary pp-btn--grow pp-btn--hero"
            href={navigation}
            target="_blank"
            rel="noreferrer"
          >
            <NavigateIcon size={18} width={2.5} />
            Navigáció
          </a>
        ) : (
          <button
            type="button"
            className="pp-btn pp-btn--primary pp-btn--grow pp-btn--hero"
            onClick={() => onOpen(trip)}
          >
            Részletek
          </button>
        )}
        {phone ? (
          <a className="pp-btn pp-btn--ghost-dark" href={phone} aria-label="Utas hívása">
            <PhoneIcon size={20} />
          </a>
        ) : null}
      </div>
    </div>
  );
}

/**
 * `now` is injectable purely so the clock is a fixture in tests — the route
 * renders without it and the screen reads the real time, ticking.
 */
export default function PortalDay({ now: clock }) {
  const navigate = useNavigate();
  const { view, setView } = useUrlViewState(DAY_VIEW);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [principal, setPrincipal] = useState(() => getPortalPrincipal());
  const [trips, setTrips] = useState(null);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [expenses, setExpenses] = useState(null);
  const [expensesError, setExpensesError] = useState('');
  const [ahead, setAhead] = useState(null);
  const ticking = useNow(!clock, CLOCK_MS);
  const now = clock || ticking;
  // Days are Budapest calendar dates; "today" moves when the Budapest day
  // does, not on every tick.
  const today = formatDate(now);
  const asked = view[DAY_PARAM];
  const selected = isCalendarDay(asked) ? asked : today;
  const isToday = selected === today;
  // One fetch per visible week, Monday to Sunday.
  const week = weekOf(selected);
  const from = week[0];
  const to = week[6];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const who = await fetchPortalSession();
        if (!cancelled && who) setPrincipal(who);
      } catch {
        // The cached principal is enough to render; the trips call is the one
        // that decides whether this session is really alive.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTrips(null);
    setError('');
    (async () => {
      try {
        const rows = await fetchPortalTrips({ from, to });
        if (!cancelled) setTrips(byTime(rows));
      } catch (failure) {
        if (!cancelled) {
          setTrips([]);
          setError(portalLoadErrorMessage(
            failure,
            'Nem sikerült betölteni a fuvarjaidat. Ellenőrizd a netkapcsolatot.',
          ));
        }
      }
    })();
    return () => { cancelled = true; };
    // `today` too: a screen left open overnight re-reads the week at midnight.
  }, [from, to, today]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchPortalExpenses();
        if (!cancelled) setExpenses(rows);
      } catch {
        // Secondary to the trips: say so quietly under its own heading.
        if (!cancelled) setExpensesError('A rögzített költségeket most nem sikerült betölteni.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onExpenseSaved = useCallback((expense) => {
    // Prepend what the API handed back rather than refetching: the driver sees
    // their ticket in the list the moment the sheet closes, even on bad signal.
    setExpenses((rows) => [expense, ...(rows || [])]);
    setExpensesError('');
  }, []);

  const openTrip = useCallback((trip) => {
    navigate(`/portal/trip/${trip._id}`, { state: { trip } });
  }, [navigate]);

  // Replaces rather than pushes (setView's default): the phone's back
  // button leaves the day screen instead of stepping back through every day
  // looked at. Today is the default, so choosing it drops `?nap=` — and
  // only `?nap=`.
  const selectDay = useCallback((day) => {
    setView({ [DAY_PARAM]: day === today ? '' : day });
  }, [setView, today]);

  const pickDay = useCallback((day) => {
    setCalendarOpen(false);
    selectDay(day);
  }, [selectDay]);

  const closeCalendar = useCallback(() => setCalendarOpen(false), []);

  const name = principalName(principal);
  const tripCounts = {};
  (trips || []).forEach((trip) => {
    const day = formatDate(trip.date_datetime);
    tripCounts[day] = (tripCounts[day] || 0) + 1;
  });
  const listed = (trips || []).filter((trip) => formatDate(trip.date_datetime) === selected);
  const hero = isToday ? pickNextTrip(listed, now) : null;
  /**
   * Where the clock sits in the day's list — everything above it is driven,
   * everything below is still to come. ONLY on today: a "most" line across
   * last Thursday's list, or Friday's, would say nothing true. `isToday` is
   * already a BUDAPEST comparison (`formatDate(now)`, above), which is the
   * same day the list itself is filtered by, so the line and the rows can
   * never be judging different days. The clock ticks (`useNow`, 30s), so
   * the line walks down a screen left open all day.
   */
  const nowAt = isToday ? nowSplitIndex(listed, now) : -1;
  // Today, loaded, and nothing left to drive: look past the visible week too.
  const lookAhead = isToday && trips !== null && !error && !hero;

  useEffect(() => {
    if (!lookAhead) {
      setAhead(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchPortalTrips({
          from: addDays(today, 1),
          to: addDays(today, LOOKAHEAD_DAYS),
        });
        if (!cancelled) setAhead(byTime(rows)[0] || null);
      } catch {
        // Only a pointer: the week strip still shows every day's trips.
        if (!cancelled) setAhead(null);
      }
    })();
    return () => { cancelled = true; };
  }, [lookAhead, today]);

  return (
    <>
      <div className="pp-header">
        <div className="pp-brand">
          <BrandLogo variant="color" height={22} />
        </div>
        <button
          type="button"
          className="pp-avatar"
          aria-label="Profil"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {initials(name) || '—'}
        </button>
      </div>

      {menuOpen ? (
        <div className="pp-stack">
          <div className="pp-card">
            <div className="pp-person__name">{name || 'Sofőr'}</div>
            <div className="pp-person__phone">Ez a telefon össze van kötve a fiókoddal.</div>
            {/*
              The one button a driver reaches for mid-shift, and until now
              it opened an EMPTY chat: the office got "Szia" and had to ask
              who and which job. It carries the spine of whatever the
              driver is on right now (officeMessage) — no state sentence,
              because from here there is no particular state.
            */}
            <OfficeWhatsAppButton
              className="pp-gap-top"
              message={officeMessage({ trip: hero, driverName: name })}
            />
            <button
              type="button"
              className="pp-btn pp-btn--outline pp-btn--block pp-gap-top"
              onClick={signOutOfPortal}
            >
              Kijelentkezés ezen a telefonon
            </button>
          </div>
          {/*
            The plain off-switch. It lives next to "sign out on this phone"
            because that is where a driver looks for "this phone" settings,
            and it is reachable on every visit — so a driver who said no (or
            was never asked) at enrolment can still turn notifications on,
            and one who wants them to stop can stop them without asking the
            office. See PushSetting for why some phones see an explanation
            instead of a switch.
          */}
          <PushSetting variant="settings" />
        </div>
      ) : null}

      <div className="pp-greet">
        <h1 className="pp-greet__name">{name ? `Szia, ${firstName(name)}!` : 'Szia!'}</h1>
        <div className="pp-greet__date">{longDate(now)}</div>
      </div>

      <WeekStrip
        selected={selected}
        today={today}
        tripCounts={tripCounts}
        onSelect={selectDay}
        onOpenCalendar={() => setCalendarOpen(true)}
      />

      {trips === null && <div className="pp-state" role="status">Fuvarok betöltése…</div>}
      {error ? <div className="pp-error" role="alert">{error}</div> : null}

      {hero ? <Hero trip={hero} onOpen={openTrip} now={now} /> : null}

      {trips !== null && !error ? (
        <>
          <div className="pp-section-head">
            <h2 className="pp-section-head__title">
              {isToday ? 'A mai napod' : longDate(selected)}
            </h2>
            {listed.length ? (
              <span className="pp-section-head__count">{`${listed.length} út`}</span>
            ) : null}
          </div>
          {listed.length ? (
            <div className="pp-list">
              {listed.map((trip, index) => (
                <React.Fragment key={trip._id}>
                  {nowAt === index ? (
                    <NowLine
                      block={NOW_LINE_PORTAL}
                      label={NOW_LABEL}
                      time={nowTimeLabel(now)}
                    />
                  ) : null}
                  <TripRow
                    trip={trip}
                    isNext={trip === hero}
                    onOpen={openTrip}
                  />
                </React.Fragment>
              ))}
              {nowAt === listed.length ? (
                <NowLine block={NOW_LINE_PORTAL} label={NOW_LABEL} time={nowTimeLabel(now)} />
              ) : null}
            </div>
          ) : (
            <div className="pp-empty">
              {isToday ? 'Ma nincs beosztott fuvarod.' : 'Erre a napra nincs beosztott fuvarod.'}
              <br />
              Ha változik, itt fogod látni.
            </div>
          )}
        </>
      ) : null}

      {ahead ? (
        <>
          <div className="pp-section-head">
            <h2 className="pp-section-head__title">Következő fuvarod</h2>
            <span className="pp-section-head__count">{dayLabel(ahead.date_datetime, now)}</span>
          </div>
          <div className="pp-list">
            <TripRow trip={ahead} onOpen={openTrip} />
          </div>
        </>
      ) : null}

      {/*
        The one time a driver already using the portal is asked about
        notifications. It sits AFTER the day's work — nothing here may come
        between a driver and their next pickup — and it is shown only to
        somebody who has never decided, on a phone that could really deliver
        (PushAsk). Waved away, it is gone for good on this phone, and the
        menu's switch above is where it lives from then on.
      */}
      <PushAsk />

      <div className="pp-grow" />

      <ExpenseList
        expenses={expenses}
        error={expensesError}
        now={now}
        trips={ahead ? [...(trips || []), ahead] : trips}
      />

      <div className="pp-day-foot">
        <RecordExpense onSaved={onExpenseSaved} />
      </div>

      {calendarOpen ? (
        <DayPickerSheet
          selected={selected}
          today={today}
          tripCounts={tripCounts}
          onPick={pickDay}
          onClose={closeCalendar}
        />
      ) : null}
    </>
  );
}
